import { computed, action } from 'mobx';
import { StoreBase } from '../../../common/StoreBase';
import { INoSearchTermPlaceholderConfig, IInternalOptions, IResultsConfiguration, TResultsType, INoResultsPlaceholderConfig } from '../models';
import { IResultLink } from '../../App/models/IResultItem';

export class ResultsStore extends StoreBase {
	static componentKey: 'results' = 'results';

	@action
	async initialize(): Promise<void> { }

	@computed
	get configuration(): IResultsConfiguration | undefined {
		if (this.storeContext && this.storeContext.appStore) {
			return this.storeContext.appStore.getComponentConfiguration(ResultsStore.componentKey);
		}
	}

	@computed
	get type(): TResultsType {
		const defaultValue = 'external';
		if (this.configuration) {
			return this.configuration.type || defaultValue;
		}
		return defaultValue;
	}

	@computed
	get noSearchTermPlaceholderConfig(): Required<INoSearchTermPlaceholderConfig> {
		const defaultConfig: Required<INoSearchTermPlaceholderConfig> = {
			show: true,
			text: {
				queryOptionsType: 'none',
				value: 'Please enter a search term to view results',
			},
		};
		return { ...defaultConfig, ...this.internalOptions?.noSearchTermPlaceholder };
	}

	@computed
	get noResultsPlaceholderConfig(): INoResultsPlaceholderConfig {
		const defaultConfig: INoResultsPlaceholderConfig = {
			text: {
				queryOptionsType: 'none',
				value: 'No results were found matching the search term',
			},
		};
		return { ...defaultConfig, ...this.internalOptions?.noResultsPlaceholder };
	}

	@computed
	get internalOptions(): IInternalOptions | undefined {
		return this.configuration?.internalOptions;
	}

	@computed
	get hasResults(): boolean {
		const { appStore } = this.storeContext;
		return (appStore.groupedResults || []).length > 0;
	}

	@computed
	get noSearchTermPlaceholder(): string {
		const { text } = this.noSearchTermPlaceholderConfig;
		return this.getQueryValue(text);
	}

	@computed
	get noResultsPlaceholder(): string {
		const { text } = this.noResultsPlaceholderConfig;
		return this.getQueryValue(text);
	}

	@computed
	get showNoResultsPlaceholder(): boolean {
		const { appStore } = this.storeContext;
		const { groupedResults } = appStore;
		return !!(groupedResults && groupedResults.length === 0);
	}

	@computed
	get showNoSearchTermPlaceholder(): boolean {
		const { searchBarStore } = this.storeContext;
		const { minimumCharacters, searchTerm } = searchBarStore;
		return minimumCharacters <= 0 && this.noSearchTermPlaceholderConfig.show && searchTerm === '';
	}

	@computed
	get show(): boolean {
		return this.type === 'internal' && (this.hasResults || this.showNoResultsPlaceholder || this.showNoSearchTermPlaceholder);
	}

	@computed
	get seeAllResultsLink(): IResultLink | undefined {
		const { appStore } = this.storeContext;
		if (this.internalOptions?.allResults && this.hasResults) {
			const { redirectLabel, redirect, hide = false } = this.internalOptions.allResults;
			if (!hide) {
				// v4 migration: Use a translation key for this and use the existing phrase as the default static text
				const label = redirectLabel || 'See all search results';
				const href = appStore.getHref(redirect);
				return {
					label,
					href,
					onClick: () => appStore.onClick(href),
				};
			}
		}
	}
}
